import { Link } from "react-router-dom";
import { Handshake, Rocket, Coins, BadgeCheck } from "lucide-react";
import GradientBg from "@/components/common/GradientBg";
import HeadMeta from "@/components/common/HeadMeta";
import Reveal from "@/components/marketing/Reveal";
import { BRAND } from "@/branding.config";

const TIERS = [
  { name: "Registered",  icon: BadgeCheck, commission: "10%", terms: "No minimum. Live in 24 hours.", features: ["Marketing kit", "Live tracking dashboard", "Monthly payouts"] },
  { name: "Silver",      icon: Rocket,     commission: "20%", terms: "$5k / quarter", featured: false, features: ["Co-marketing budget", "Priority support", "Bi-weekly payouts"] },
  { name: "Gold",        icon: Handshake,  commission: "30%", terms: "$25k / quarter", featured: true, features: ["Dedicated account manager", "Custom landing pages", "Weekly payouts", "Deal registration"] },
];

const BRANDS = ["MetaQuotes", "cTrader", "NinjaTrader", "TradingView", "Bloomberg", "Refinitiv", "Interactive Brokers", "OANDA"];

export default function Partners() {
  return (
    <>
      <HeadMeta title="Partners" description={`Reseller, affiliate and technology partnerships with ${BRAND.app_name}.`} />
      <div className="relative">
        <GradientBg variant="hero" />

        <section className="relative mx-auto max-w-6xl px-6 pt-28 pb-16">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Partners</div>
            <h1 className="mt-4 text-5xl md:text-7xl font-black tracking-tight max-w-3xl">
              Build a business on <span className="brand-gradient-text">sub-1ms.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              Whether you're a broker, IB, trading educator or platform integrator, our partner
              program gives you the tooling and margin to grow. Live tracking, transparent
              payouts, and a team that answers.
            </p>
            <div className="mt-10 flex gap-3">
              <Link to="/contact" className="btn-primary px-6 py-3">Become a partner</Link>
              <Link to="/kb"      className="btn-outline px-6 py-3">Read the program</Link>
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Tiers</div>
              <h2 className="mt-3 text-4xl font-black tracking-tight">Three tiers. Transparent economics.</h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {TIERS.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.name} delay={i * 0.1}>
                  <div className={`glass p-8 h-full relative overflow-hidden ${t.featured ? "border-primary/40 glow-violet" : ""}`}>
                    {t.featured && (
                      <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/25">
                        Most popular
                      </span>
                    )}
                    <Icon className="text-primary" size={22}/>
                    <div className="mt-5 text-xl font-bold">{t.name}</div>
                    <div className="mt-6 font-display text-5xl font-black brand-gradient-text">{t.commission}</div>
                    <div className="mt-1 text-xs text-muted-foreground">recurring commission</div>
                    <div className="mt-4 text-sm text-muted-foreground">{t.terms}</div>
                    <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
                      {t.features.map((f) => <li key={f}>• {f}</li>)}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <div className="text-center text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Technology partners &amp; brokers integrated with our network
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
              {BRANDS.map((b) => (
                <div key={b} className="font-display text-xl font-bold tracking-tight text-muted-foreground">{b}</div>
              ))}
            </div>
          </Reveal>
        </section>
      </div>
    </>
  );
}
